/** "Link evidence" section for the MeterDetail panel (spec 2026-09-02 section 6
 *  Phase 5.4). Three opinions about which transformer feeds this meter, side by
 *  side: the GIS attribute (XFMR_ID), the pole evidence (PT_XFMR_LOC) and the
 *  current published secondary run's attachment — each agreeing or disagreeing
 *  with the link the platform holds today.
 *
 *  READ-ONLY. Settling a disagreement is Accept (5.1), the existing assign, or a
 *  GIS edit; nothing here writes a link. The server always sends three opinions
 *  in a fixed order, so the three columns never move: a source with no evidence
 *  reads "none" / "No match" / Unknown rather than disappearing. */

import type { LinkOpinion, MeterLinkOpinions, OpinionSource } from '../../types/linkEvidence.ts'

const COLUMN_TITLES: Record<OpinionSource, string> = {
  gis_attribute: 'GIS attribute',
  pole: 'Pole',
  secondary: 'Secondary network',
}

/** Unknown is GRAY, never amber: "no evidence" and "no link" are not
 *  disagreements, and colouring them like one sends reviewers chasing
 *  nothing. */
const CHIPS = {
  agrees: {
    label: 'Agrees',
    color: 'bg-green-100 text-green-700',
    title: 'This evidence names the transformer the meter is linked to',
  },
  disagrees: {
    label: 'Disagrees',
    color: 'bg-amber-100 text-amber-700',
    title: 'This evidence names a different transformer than the meter is linked to',
  },
  unknown: {
    label: 'Unknown',
    color: 'bg-gray-100 text-gray-600',
    title: 'No candidate from this source, or the meter has no link to compare against',
  },
} as const

interface LinkOpinionsSectionProps {
  opinions: MeterLinkOpinions | undefined
  onNavigate: (type: string, id: number) => void
}

export function LinkOpinionsSection({ opinions, onNavigate }: LinkOpinionsSectionProps) {
  if (!opinions) return null
  return (
    <div className="border-t border-gray-200 pt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Link evidence
      </p>
      <div className="grid grid-cols-3 gap-2">
        {opinions.opinions.map((opinion) => (
          <OpinionColumn key={opinion.source} opinion={opinion} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  )
}

function OpinionColumn({
  opinion,
  onNavigate,
}: {
  opinion: LinkOpinion
  onNavigate: (type: string, id: number) => void
}) {
  const pick = opinion.transformer_id
  return (
    <div className="min-w-0 p-2 rounded bg-gray-50">
      <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide truncate">
        {COLUMN_TITLES[opinion.source]}
      </div>
      <div className="mt-0.5 text-xs text-gray-600 truncate" title={opinion.reference ?? undefined}>
        {opinion.reference ?? 'none'}
      </div>
      {pick != null ? (
        <button
          type="button"
          onClick={() => onNavigate('Transformer', pick)}
          className="mt-1 block w-full text-left text-sm font-medium text-blue-700 hover:underline truncate"
        >
          {opinion.transformer_name || `Transformer #${pick}`}
        </button>
      ) : (
        <div className="mt-1 text-sm text-gray-400">No match</div>
      )}
      {opinion.via && <div className="text-[11px] text-gray-400 truncate">via {opinion.via}</div>}
      {opinion.detail && <div className="text-[11px] text-gray-400">{opinion.detail}</div>}
      {opinion.candidates.length > 1 && (
        <div className="text-[11px] text-gray-400">{opinion.candidates.length} candidates</div>
      )}
      <div className="mt-1">
        <AgreesChip agrees={opinion.agrees} />
      </div>
    </div>
  )
}

function AgreesChip({ agrees }: { agrees: boolean | null }) {
  const key = agrees === true ? 'agrees' : agrees === false ? 'disagrees' : 'unknown'
  const chip = CHIPS[key]
  return (
    <span
      title={chip.title}
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${chip.color}`}
    >
      {chip.label}
    </span>
  )
}
